import { BIO_ALLOWED_SCHEMES, BIO_LINK_REL, BIO_LINK_TARGET } from '@onesource/schemas';
import { Extension, Node, mergeAttributes } from '@tiptap/core';
import { Subscript } from '@tiptap/extension-subscript';
import { Superscript } from '@tiptap/extension-superscript';
import { TableKit } from '@tiptap/extension-table';
import { StarterKit } from '@tiptap/starter-kit';

const PASSTHROUGH_TARGETS = [
  'paragraph',
  'heading',
  'blockquote',
  'bulletList',
  'orderedList',
  'listItem',
  'codeBlock',
  'link',
];

const passthroughAttribute = (name: string) => ({
  default: null as string | null,
  parseHTML: (element: HTMLElement) => element.getAttribute(name),
  renderHTML: (attributes: Record<string, unknown>) => {
    const v = attributes[name];
    return typeof v === 'string' && v.length > 0 ? { [name]: v } : {};
  },
});

const PASSTHROUGH_HTML_ATTRS = {
  class: passthroughAttribute('class'),
  style: passthroughAttribute('style'),
  id: passthroughAttribute('id'),
};

const PassthroughAttributes = Extension.create({
  name: 'passthroughAttributes',
  addGlobalAttributes() {
    return [{ types: PASSTHROUGH_TARGETS, attributes: PASSTHROUGH_HTML_ATTRS }];
  },
});

const HtmlButton = Node.create({
  name: 'htmlButton',
  group: 'inline',
  inline: true,
  content: 'inline*',
  selectable: true,
  defining: true,
  addAttributes() {
    return {
      ...PASSTHROUGH_HTML_ATTRS,
      type: {
        default: 'button',
        parseHTML: (el: HTMLElement) => el.getAttribute('type') ?? 'button',
        renderHTML: (attrs: Record<string, unknown>) => ({ type: (attrs.type as string) ?? 'button' }),
      },
      'data-href': passthroughAttribute('data-href'),
    };
  },
  parseHTML() {
    return [{ tag: 'button' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['button', mergeAttributes(HTMLAttributes), 0];
  },
});

const HtmlDiv = Node.create({
  name: 'htmlDiv',
  group: 'block',
  content: 'block+',
  defining: true,
  addAttributes() {
    return PASSTHROUGH_HTML_ATTRS;
  },
  parseHTML() {
    return [{ tag: 'div', priority: 51 }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes), 0];
  },
});

export const HtmlImage = Node.create({
  name: 'htmlImage',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      ...PASSTHROUGH_HTML_ATTRS,
      src: passthroughAttribute('src'),
      alt: passthroughAttribute('alt'),
      title: passthroughAttribute('title'),
      width: passthroughAttribute('width'),
      height: passthroughAttribute('height'),
    };
  },
  parseHTML() {
    return [{ tag: 'img[src]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['img', mergeAttributes(HTMLAttributes)];
  },
});

/**
 * Any `<iframe src>` in the authored HTML, kept as a real node so an embed
 * round-trips through the editor instead of being stripped on parse. Video
 * embeds (Vimeo) are inserted through this node — see `toVimeoEmbedSrc`.
 *
 * `allowfullscreen` / `frameborder` are re-applied on render rather than carried
 * as attributes: `allowfullscreen` is a boolean attribute, so `getAttribute`
 * returns `''` when it is present and the passthrough helper (which only emits
 * non-empty strings) would silently drop it.
 */
export const HtmlIframe = Node.create({
  name: 'htmlIframe',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      ...PASSTHROUGH_HTML_ATTRS,
      src: passthroughAttribute('src'),
      title: passthroughAttribute('title'),
      width: passthroughAttribute('width'),
      height: passthroughAttribute('height'),
      allow: passthroughAttribute('allow'),
    };
  },
  parseHTML() {
    return [{ tag: 'iframe[src]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['iframe', mergeAttributes(HTMLAttributes, { allowfullscreen: 'true', frameborder: '0' })];
  },
});

/**
 * Turn whatever an admin pastes for a Vimeo video into a player embed URL.
 * Accepts a bare id (`76979871`), a share link, a channel link, or an already
 * built player URL. Unlisted videos carry a privacy hash as a second path
 * segment (`vimeo.com/76979871/abcdef1234`) which the player needs as `?h=`,
 * so it is preserved rather than dropped.
 *
 * Returns null when nothing video-shaped is found, so the caller can tell the
 * admin instead of inserting a broken frame.
 */
export const toVimeoEmbedSrc = (input: string): string | null => {
  const trimmed = input.trim();
  if (trimmed === '') {
    return null;
  }

  if (/^\d+$/.test(trimmed)) {
    return `https://player.vimeo.com/video/${trimmed}`;
  }

  // Already a player URL — keep it (and its query) as-is.
  const playerMatch = trimmed.match(/player\.vimeo\.com\/video\/(\d+)(\?[^\s]*)?/i);
  if (playerMatch) {
    return `https://player.vimeo.com/video/${playerMatch[1]}${playerMatch[2] ?? ''}`;
  }

  const shareMatch = trimmed.match(/vimeo\.com\/(?:[\w-]+\/)*?(\d+)(?:\/([\da-z]+))?/i);
  if (shareMatch) {
    const hash = shareMatch[2] ? `?h=${shareMatch[2]}` : '';
    return `https://player.vimeo.com/video/${shareMatch[1]}${hash}`;
  }

  return null;
};

export const EXTENSIONS = [
  StarterKit,
  PassthroughAttributes,
  HtmlButton,
  HtmlDiv,
  HtmlImage,
  HtmlIframe,
  // Resizable columns so an admin can size a table to its content; the widths
  // ride along as inline styles, which the passthrough attributes preserve.
  TableKit.configure({ table: { resizable: true } }),
];

const BIO_URI_PATTERN = new RegExp(`^(${BIO_ALLOWED_SCHEMES.join('|')}):`, 'i');

/**
 * Strict set for the Pro Directory bio: bold, italic, underline, lists, sub/sup
 * and links — nothing else. No passthrough attributes and no div/image/iframe/
 * table/heading nodes, so pasted or loaded HTML outside the allow-list is dropped
 * by the ProseMirror schema before it reaches `getHTML()`.
 */
export const BIO_EXTENSIONS = [
  StarterKit.configure({
    heading: false,
    blockquote: false,
    codeBlock: false,
    code: false,
    strike: false,
    horizontalRule: false,
    link: {
      openOnClick: false,
      autolink: false,
      linkOnPaste: false,
      isAllowedUri: url => BIO_URI_PATTERN.test(url),
      HTMLAttributes: { target: BIO_LINK_TARGET, rel: BIO_LINK_REL },
    },
  }),
  Subscript,
  Superscript,
];
